import { useContext, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { Select } from '@/isomorphic/components/forms/components/select';
import { Checkbox } from '@/isomorphic/components/forms/components/checkbox';
import { Field } from '@/isomorphic/components/forms/components/field';
import { Quote } from './quote';
import { InfoButton } from './info-button';
import { TermsBox } from './terms-box';
import { Button } from '@/isomorphic/components/typography/button/button';
import { useSupremeCards } from '@/hooks/use-supreme-cards';
import { validators } from '@/hooks/use-validation';
import { useLocalization } from '@/hooks/use-localization';
import { CardTerms } from './card-terms';
import { FieldsType, SupremeCardApplicationLabels } from '../types';
import { AppContext } from '@/contexts/app-context';
import { ConsumerApplicationProduct, useDataLayer } from '@/hooks/use-data-layer';

export type StepOneProps = {
    fields: FieldsType;
    defaultCard: string;
    isLoading: boolean;
    updateField: (field: Partial<FieldsType>) => void;
    labels: SupremeCardApplicationLabels;
    onSubmit: (step: number) => void;
    useMAPI?: boolean;
    fieldErrors?: Record<string, boolean>;
    setFieldErrors?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    shouldFocusError?: boolean;
    setShouldFocusError?: React.Dispatch<React.SetStateAction<boolean>>;
    hiddenCards?: string[];
};

export const StepOne = ({
    fields,
    updateField,
    labels,
    onSubmit,
    isLoading,
    useMAPI = false,
    fieldErrors,
    setFieldErrors,
    shouldFocusError,
    setShouldFocusError,
    hiddenCards = [],
}: StepOneProps) => {
    const context = useContext(AppContext);
    const { dataLayerPush } = useDataLayer();
    const { getLanguage, getLocale } = useLocalization();
    const [searchParams, setSearchParams] = useSearchParams();
    const [cardInfoOpen, setCardInfoOpen] = useState(false);
    const formRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!shouldFocusError || !formRef.current) return;

        // Find the first element with aria-invalid="true" within this form
        const firstInvalid = formRef.current.querySelector('[aria-invalid="true"]');
        if (firstInvalid instanceof HTMLElement) {
            // For inputs/textareas, focus directly. For other elements (like combobox), find focusable child
            if (firstInvalid.tagName === 'INPUT' || firstInvalid.tagName === 'TEXTAREA') {
                firstInvalid.focus();
            } else {
                const focusable = firstInvalid.querySelector<HTMLElement>('input, textarea, [tabindex="0"]');
                if (focusable) {
                    focusable.focus();
                }
            }
        }

        setShouldFocusError?.(false);
    }, [shouldFocusError, setShouldFocusError]);

    const {
        supremeCards: { data: allCards },
    } = useSupremeCards();
    const cards = allCards?.filter(({ api_name }) => !hiddenCards?.includes(api_name)); // eslint-disable-line camelcase

    useEffect(() => {
        if (fields.selectedCard && hiddenCards?.includes(fields.selectedCard)) {
            updateField({ selectedCard: '' });
        }
    }, [fields.selectedCard, hiddenCards]);

    const updateCardChoice = (card: string) => {
        updateField({ selectedCard: card as string });
        context.setSupremeCardApplication({ selectedCard: card });

        // Keep the `selectedCard` URL param in sync with the user's choice so
        // the URL stays shareable/reloadable. Replace (don't push) so toggling
        // cards doesn't pollute the back-button history.
        const params = new URLSearchParams(searchParams);
        if (card) {
            params.set('selectedCard', card);
        } else {
            params.delete('selectedCard');
        }
        setSearchParams(params, { replace: true });

        dataLayerPush({
            event: 'ApplicationStart',
            Product: ConsumerApplicationProduct['CREDIT_CARD'],
            ProductVariant: card || '',
        });
    };

    const isSumbitDisabled = () => isLoading;

    const clearFieldError = (fieldName: string) => {
        if (fieldErrors?.[fieldName] && setFieldErrors) {
            setFieldErrors(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    const cardOptions = [{ api_name: '', post_title: labels.selectCard }, ...(cards || [])]; // eslint-disable-line camelcase

    return (
        <div ref={formRef}>
            <div className="r-mb-8 r-space-y-6">
                {/* The SSR makes this weird for some reason, so we'll wait until the cards are loaded before rendering */}
                {allCards?.length ? (
                    <Select
                        value={fields.selectedCard}
                        infoText={labels.selectCardInfoText}
                        label={labels.selectCard}
                        onChange={value => {
                            updateCardChoice(value as string);
                            clearFieldError('selectCard');
                        }}
                        options={cardOptions?.map(({ api_name: value, post_title: label }) => ({ value, label })) || []}
                        showRequiredError={!!fieldErrors?.selectCard}
                        errorMessage={labels.selectCardErrorText || 'Please choose a card'}
                    />
                ) : null}
                <div
                    className={clsx('r-cursor-pointer r-bg-gray-200 r-px-4 r-py-2 md:r-hidden', {
                        'r-hidden': !context.supremeCardApplication.progressText?.title,
                    })}
                    onClick={() => setCardInfoOpen(!cardInfoOpen)}
                >
                    <div className="r-flex r-items-center r-justify-between">
                        <h4 className="r-font-medium">{context.supremeCardApplication.progressText?.title}</h4>
                        <span className="r-text-3xl">{cardInfoOpen ? '-' : '+'}</span>
                    </div>
                    <div
                        className={clsx({
                            'r-hidden': !cardInfoOpen,
                        })}
                    >
                        <div
                            className="styled-list r-mt-2 r-pb-2"
                            dangerouslySetInnerHTML={{ __html: context.supremeCardApplication.progressText?.content || '' }}
                        />
                    </div>
                </div>
                <div>
                    <Field
                        grouped="top"
                        value={fields.personalNumber}
                        label={`${labels.personalNumber} *`}
                        fieldName="personalNumber"
                        infoText={labels.personalNumberInfoText}
                        showRequiredError={!!fieldErrors?.personalNumber}
                        onChange={value => {
                            updateField({ personalNumber: value });
                            clearFieldError('personalNumber');
                        }}
                        validation={value => {
                            if (getLanguage() === 'sv') {
                                return validators.socialSecurityNumber(value.replace(/[- ]+/, ''), getLanguage());
                            }
                            return validators.socialSecurityNumber(value, getLanguage());
                        }}
                        errorMessage={labels.personalNumberErrorText || 'Please enter a valid personal number'}
                    />
                    <Field
                        grouped="middle"
                        value={fields.contactEmail}
                        label={`${labels.contactEmail} *`}
                        fieldName="contactEmail"
                        infoText={labels.contactEmailInfoText}
                        showRequiredError={!!fieldErrors?.contactEmail}
                        onChange={value => {
                            updateField({ contactEmail: value });
                            clearFieldError('contactEmail');
                        }}
                        validation={validators.email}
                        errorMessage={labels.contactEmailErrorText || 'Please enter a valid email address'}
                    />
                    <Field
                        grouped="bottom"
                        value={fields.phoneMobile}
                        label={`${labels.phoneMobile} *`}
                        fieldName="phoneMobile"
                        infoText={labels.phoneMobileInfoText}
                        showRequiredError={!!fieldErrors?.phoneMobile}
                        onChange={value => {
                            updateField({ phoneMobile: value });
                            clearFieldError('phoneMobile');
                        }}
                        validation={value => validators.phoneMobile(value, getLocale())}
                        errorMessage={labels.phoneMobileErrorText || 'Please enter a valid mobile phone number'}
                    />
                </div>
            </div>
            <div className="r-space-y-6">
                {!useMAPI ? (
                    <>
                        <Quote quote={labels.pepInfoBoxContent.replace(/(<([^>]+)>)/gi, '')} />
                        <div>
                            <label className="r-block r-pb-2 r-font-bold">{labels.pepQuestion}</label>
                            <Select
                                infoText={labels.selectCardInfoText}
                                label={labels.pepAnswersPlaceholder}
                                placeholder={labels.pepAnswersPlaceholder}
                                value={fields.pepOptions}
                                showRequiredError={!!fieldErrors?.pepOptions}
                                onChange={value => {
                                    updateField({ pepOptions: value as string });
                                    clearFieldError('pepOptions');
                                }}
                                options={labels.pepAnswers.split(',').map(answer => ({ value: answer, label: answer }))}
                            />
                        </div>
                        <InfoButton label={labels.pepInfoBox} infoText={labels.pepInfoBoxContent.replace(/(<([^>]+)>)/gi, '')} />
                        <InfoButton label={labels.knownEmployeeTitle} infoText={labels.knownEmployeeContent.replace(/(<([^>]+)>)/gi, '')} />
                        <InfoButton label={labels.familyMemberTitle} infoText={labels.familyMemberContent.replace(/(<([^>]+)>)/gi, '')} />
                    </>
                ) : null}
                <div className="r-space-y-2">
                    <p className="r-text-sm r-font-bold">{labels.acceptTerms}</p>
                    <TermsBox terms={labels.termsContent.replace(/(<([^>]+)>)/gi, '')} />
                    <div className="r-pt-4">
                        <Checkbox
                            label={labels.acceptTerms}
                            checked={fields.acceptTerms}
                            showRequiredError={!!fieldErrors?.acceptTerms}
                            onChange={value => {
                                updateField({ acceptTerms: value });
                                clearFieldError('acceptTerms');
                            }}
                        />
                    </div>
                </div>
                <CardTerms />
                <Button fullWidth disabled={isSumbitDisabled()} variant="bankidwhite" onClick={() => onSubmit(1)}>
                    {labels.buttonLoginAndProceed}
                </Button>
            </div>
        </div>
    );
};
